import { Box, Flex, Spinner, Text } from "@chakra-ui/react";
import { FeatureCollection } from "geojson";
import { useEffect } from "react";
import { RiInformationLine } from "react-icons/ri";
import { Pane } from "react-leaflet";
import { TextGray } from "components/Atomos/Texts/TextGray";
import { PopupMap } from "components/Organismos/Map/PopupNotification/PopupMap";
import { useAppDispatch, useTypedSelector } from "store/hooks";
import { obterZonasGeo } from "store/modules/consultaprevia/actions/obterZonasGeo";
import { obterAreasEspeciaisGeo } from "store/modules/consultaprevia/actions/obterAreasEspeciaisGeo";
import {
  chaveDaFeicao,
  OrigemCamada,
  useCamadas
} from "../contexts/CamadasProvider";
import { CamadaGeoJson } from "./CamadaGeoJson";

const PANE_ZONAS = "consulta-previa-zonas";
const PANE_AREAS = "consulta-previa-areas";

// overlayPane é 400. As duas camadas ficam logo acima, e o empilhamento é do
// plano — não da ordem em que os componentes montam. Sem isso, bastaria o
// cidadão desligar e religar as zonas para elas voltarem por cima das áreas.
const Z_INDEX_ZONAS = { zIndex: 410 };
const Z_INDEX_AREAS = { zIndex: 420 };

/**
 * As duas camadas do mapa. Zero-prop de propósito: filho de <Map> lê o próprio
 * estado, porque props passadas pela fronteira do mapa envelhecem.
 */
export const CamadasConsultaPrevia = () => {
  const dispatch = useAppDispatch();
  const { ocultas, estaVisivel, siglaSelecionada } = useCamadas();

  const zonas = useTypedSelector((state) => state.consultaprevia.obterZonasGeo);
  const areas = useTypedSelector(
    (state) => state.consultaprevia.obterAreasEspeciaisGeo
  );

  // As duas coleções vêm inteiras uma vez: são a versão publicada, não mudam
  // enquanto a tela está aberta.
  useEffect(() => {
    dispatch(obterZonasGeo({}));
    dispatch(obterAreasEspeciaisGeo({}));
  }, [dispatch]);

  // Só as camadas ligadas vão para o mapa. Filtrar aqui (e não esconder via
  // estilo) evita que uma mancha desligada continue capturando o clique.
  const visiveis = (
    colecao: FeatureCollection,
    origem: OrigemCamada
  ): FeatureCollection => ({
    ...colecao,
    features: colecao.features.filter((feature) =>
      estaVisivel(chaveDaFeicao(feature, origem))
    )
  });

  const carregando = zonas.isRequesting || areas.isRequesting;

  // As duas respostas chegaram e nenhuma trouxe camada. Isso acontece sem versão
  // publicada e também com uma versão publicada sem nenhuma zona ou área ativa:
  // o backend devolve coleção vazia nos dois casos, e para quem consulta dá no
  // mesmo — não há zoneamento a ver. O aviso existe porque, calado, o resultado
  // é um mapa em branco e sem controle de camadas, que se lê como tela quebrada.
  // Só a coleção vazia entra aqui: falha de rede deixa `successPayload` nulo, e
  // dizer "nada publicado" quando a resposta nem chegou seria mentira.
  const semCamadas =
    !carregando &&
    zonas.successPayload?.features.length === 0 &&
    areas.successPayload?.features.length === 0;

  // `ocultas` entra na chave para a camada se redesenhar a cada marcação da
  // lista — o <GeoJSON> não reage à troca de `data`.
  const assinatura = ocultas.join("|");

  return (
    <>
      {carregando && (
        <PopupMap>
          <Flex align="center">
            <Text fontSize="lg">Carregando o plano diretor</Text>
            <Spinner size="sm" ml="2" color="gray.500" />
          </Flex>
        </PopupMap>
      )}

      {semCamadas && (
        <PopupMap maxW="min(100vw - 20px, 26rem)">
          <Flex align="flex-start" gap="2">
            <Box color="orange.400" flexShrink={0} mt="0.5">
              <RiInformationLine size={20} />
            </Box>
            <Box>
              <Text fontSize="sm" fontWeight="bold">
                Nada publicado para consultar
              </Text>
              <TextGray fontSize="xs">
                O plano diretor ainda não tem zonas urbanas nem áreas especiais
                publicadas. Assim que a prefeitura publicar uma versão, ela
                aparece neste mapa.
              </TextGray>
            </Box>
          </Flex>
        </PopupMap>
      )}

      <Pane name={PANE_ZONAS} style={Z_INDEX_ZONAS}>
        {zonas.successPayload && (
          <CamadaGeoJson
            colecao={visiveis(zonas.successPayload, "zona")}
            chave={`zonas-${zonas.requestNumber}-${assinatura}`}
            siglaSelecionada={siglaSelecionada}
            rotulosPermanentes
          />
        )}
      </Pane>

      <Pane name={PANE_AREAS} style={Z_INDEX_AREAS}>
        {areas.successPayload && (
          <CamadaGeoJson
            colecao={visiveis(areas.successPayload, "area")}
            chave={`areas-${areas.requestNumber}-${assinatura}`}
            siglaSelecionada={siglaSelecionada}
          />
        )}
      </Pane>
    </>
  );
};
